import { toCanvas } from 'html-to-image';
import { getBackgroundManager } from '../background/backgroundInstance.js';
import { WORLDS_SECTION_ID } from '../nav/anchors.js';
import { transitionLog } from '../debug/transitionDebug.js';
import { runChannelWipe } from '../transitions/channelWipe.js';
import { wantsSurfGame } from './tbd/surf/detect.js';
import { mountChannelBoot } from './tbd/surf/bootScreen.js';

/**
 * In-place "worlds" (work/selected, mild-concern, chotbat) on the home page.
 * History: pushState to /{world}/ (address bar) + browser Back closes the overlay.
 * Breadcrumb + logo: same WebGL pixel wipe as entering a world, revealing the
 * home page. Popstate keeps an instant close.
 */

const LOADERS = {
  chotbat: () => import('./chotbat/chotbat.js'),
  work: () => import('./work/work.js'),
  'mild-concern': () => import('./tbd/tbd.js'),
};

const IN_PLACE_WORLDS = Object.keys(LOADERS);

const IN_PLACE_TITLES = {
  chotbat: 'Chotbat \u2014 Sean Burles',
  work: 'About \u2014 Sean Burles',
  'mild-concern': 'Capt Mild Concern \u2014 Sean Burles',
};

const IN_PLACE_CRUMB = {
  chotbat: 'Chotbat',
  work: 'About',
  'mild-concern': 'Capt Mild Concern',
};

const WANTS_REDUCED_WIPE =
  typeof matchMedia === 'function' && matchMedia('(prefers-reduced-motion: reduce)').matches;


class WorldManagerClass {
  constructor() {
    this._open = false;
    this._closing = false;
    this._instance = null;
    this._content = null;
    this._overlay = null;
    this._shell = null;
    this._breadcrumb = null;
    this._crumbLabel = null;
    this._crumbLink = null;
    this._historyPushed = false;
    this._titleBefore = null;
    this._popstateBound = false;
  }

  init() {
    this._overlay = document.getElementById('worldOverlay');
    this._content = document.getElementById('worldContent');
    this._shell = document.getElementById('worldShell');
    this._breadcrumb = document.getElementById('worldBreadcrumb');
    this._crumbLabel = document.getElementById('worldCrumbLabel');
    this._crumbLink = document.getElementById('worldCrumbLink');
    this._installHistory();
    this._installBreadcrumb();
  }

  _installHistory() {
    if (this._popstateBound) return;
    this._popstateBound = true;
    window.addEventListener('popstate', () => {
      transitionLog('world:popstate', { open: this._open, closing: this._closing, path: location.pathname });
      if (!this._open) return;
      const first = (location.pathname || '/').split('/').filter(Boolean)[0];
      if (first && IN_PLACE_WORLDS.includes(first)) return;
      transitionLog('world:close_from_popstate', {});
      this.close({ skipHistory: true, force: this._closing });
    });
  }

  _installBreadcrumb() {
    if (this._crumbLink) {
      this._crumbLink.addEventListener('click', (e) => {
        e.preventDefault();
        if (this._open) this.closeWithPixelReveal();
      });
    }
    const logo = document.querySelector('#sidebar a.sidebar__logo[href^="#"]');
    if (logo) {
      logo.addEventListener('click', (e) => {
        if (!document.body.classList.contains('is-world-open')) return;
        e.preventDefault();
        this.closeWithPixelReveal();
      });
    }
  }

  _syncBreadcrumb(visible, shortName = '') {
    if (!this._breadcrumb) return;
    if (visible) {
      this._breadcrumb.removeAttribute('hidden');
      if (this._crumbLabel) this._crumbLabel.textContent = shortName;
    } else {
      this._breadcrumb.setAttribute('hidden', '');
    }
  }

  _inPlaceTitle(worldName) {
    if (worldName === 'mild-concern' && wantsSurfGame()) return 'Channel Surfing \u2014 Sean Burles';
    return IN_PLACE_TITLES[worldName] || `${worldName} \u2014 Sean Burles`;
  }

  _inPlaceCrumb(worldName) {
    if (worldName === 'mild-concern' && wantsSurfGame()) return 'Channel Surfing';
    return IN_PLACE_CRUMB[worldName] || worldName;
  }

  _applyInPlaceUrl(worldName) {
    this._titleBefore = document.title;
    document.title = this._inPlaceTitle(worldName);
    document.body.classList.add('is-world-open');
    this._openWorld = worldName;
    document.dispatchEvent(new CustomEvent('world:open', { detail: { world: worldName } }));
    this._syncBreadcrumb(true, this._inPlaceCrumb(worldName));
    this._historyPushed = true;
    try {
      history.pushState(
        { worldInPlace: true, world: worldName },
        '',
        `/${worldName}/`
      );
    } catch {
      this._historyPushed = false;
    }
  }

  /**
   * @param {boolean} [opt.skipHistory] — from popstate (path already at /)
   * @param {boolean} [opt.hard] — from _hardClose: replaceState only
   */
  _clearInPlaceUrl(opt = {}) {
    const { skipHistory = false, hard = false } = opt;
    transitionLog('world:clearUrl', { skipHistory, hard, path: location.pathname, historyPushed: this._historyPushed });
    document.body.classList.remove('is-world-open');
    document.dispatchEvent(new CustomEvent('world:close', { detail: { world: this._openWorld || null } }));
    this._openWorld = null;
    this._syncBreadcrumb(false);
    if (this._titleBefore != null) {
      document.title = this._titleBefore;
    }
    this._titleBefore = null;

    if (hard) {
      if (this._historyPushed) {
        try {
          const u = new URL(location.href);
          u.pathname = '/';
          history.replaceState(null, '', u.pathname + u.search + u.hash);
        } catch {
          /* ignore */
        }
      }
      this._historyPushed = false;
      this._queueLandOnWorlds();
      return;
    }

    if (skipHistory) {
      this._historyPushed = false;
      this._queueLandOnWorlds();
      return;
    }
    if (this._historyPushed) {
      this._historyPushed = false;
      try {
        history.back();
      } catch {
        try {
          history.replaceState(null, '', '/');
        } catch {
          /* ignore */
        }
      }
    } else {
      this._historyPushed = false;
    }

    this._queueLandOnWorlds();
  }

  _queueLandOnWorlds() {
    const p = (location.pathname || '/').replace(/\/+$/, '') || '/';
    if (p !== '/' && p !== '/index.html') {
      transitionLog('world:land:skip', { p, reason: 'not home' });
      return;
    }
    transitionLog('world:land:queue', { p });
    setTimeout(() => this._landOnWorlds(), 0);
  }

  _landOnWorlds() {
    const p = (location.pathname || '/').replace(/\/+$/, '') || '/';
    if (p !== '/' && p !== '/index.html') {
      transitionLog('world:land:skip', { p, reason: 'not home (land)' });
      return;
    }
    try {
      const u = new URL(location.href);
      u.hash = WORLDS_SECTION_ID;
      history.replaceState(null, '', u.pathname + u.search + u.hash);
      transitionLog('world:land:done', { hash: u.hash, href: u.pathname + u.search + u.hash });
    } catch {
      /* ignore */
    }
    this._scrollWorldsIntoView();
  }

  _scrollWorldsIntoView() {
    const el = document.getElementById(WORLDS_SECTION_ID);
    if (!el) return;
    if (window.lenis) {
      window.lenis.scrollTo(el, { offset: -4, duration: 0.55 });
    } else {
      el.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }

  _scrollHomeTop() {
    if (window.lenis) {
      window.lenis.scrollTo(0, { duration: 0, immediate: true, force: true });
    } else {
      window.scrollTo(0, 0);
    }
  }

  _tearDownWorldDom() {
    this._instance?.destroy?.();
    this._instance = null;
    if (this._content) {
      this._content.innerHTML = '';
      this._content.style.opacity = '0';
      this._content.style.transition = '';
    }
    if (this._overlay) {
      this._overlay.setAttribute('hidden', '');
      this._overlay.setAttribute('aria-hidden', 'true');
      this._overlay.style.zIndex = '';
      this._overlay.style.opacity = '';
      this._overlay.style.visibility = '';
      delete this._overlay.dataset.world;
    }
  }

  /**
   * @param {string} worldName
   * @param {HTMLElement} [_cardEl] — from carousel; kept for a stable call site
   * @param {DOMRect} [_rect] — from expand
   */
  /**
   * Mount in-place world HTML under the fullscreen card. The card + GL pixel
   * pass (see `runChannelWipe`) handle the visual handoff — no 2D
   * dither here.
   * @returns {Promise<void>}
   */
  async open(worldName, _cardEl, _rect) {
    transitionLog('world:open:start', { world: worldName, wasOpen: this._open, closing: this._closing });
    if (!this._overlay || !this._content) this.init();
    if (!this._overlay || !LOADERS[worldName]) {
      console.warn(`[WorldManager] unknown or missing world: ${worldName}`);
      return;
    }

    this._open = true;

    this._applyInPlaceUrl(worldName);

    // Tag the overlay with the world name so CSS overrides (e.g. Chotbat's
    // custom shell padding) apply BEFORE the first layout pass.
    this._overlay.dataset.world = worldName;

    this._overlay.removeAttribute('hidden');
    this._overlay.setAttribute('aria-hidden', 'false');
    this._overlay.style.zIndex = '2000';
    this._overlay.style.visibility = 'hidden';
    this._overlay.style.opacity = '1';

    this._content.style.opacity = '1';
    this._content.style.transition = 'none';
    this._content.innerHTML = '';
    if (worldName === 'mild-concern') mountChannelBoot(this._content);

    if (window.lenis?.stop) window.lenis.stop();

    // Kick off module import + font loading in parallel so fonts are warm
    // by the time the DOM is built (card-expand animation masks the wait).
    const fontPromise = document.fonts?.load
      ? Promise.all([
          document.fonts.load('400 1em "Anton"'),
          document.fonts.load('400 1em "DM Mono"'),
          document.fonts.load('400 1em "Barlow Condensed"'),
        ]).catch(() => {})
      : Promise.resolve();

    const [{ default: Mod }] = await Promise.all([
      LOADERS[worldName](),
      fontPromise,
    ]);

    this._instance?.destroy?.();
    this._instance = new Mod(this._content);
    this._instance.init();

    // DOM built + fonts loaded — give the browser frames to fully resolve
    // layout (including :has() / data-world overrides) before painting.
    await fontPromise;
    await new Promise((r) => requestAnimationFrame(() =>
      requestAnimationFrame(() => requestAnimationFrame(r))
    ));

    // Now make the overlay visible. The card cover layer is still on top,
    // so the user sees only the cover until the pixel reveal wipe runs.
    this._overlay.style.visibility = 'visible';
    await new Promise((r) => requestAnimationFrame(r));
    transitionLog('world:open:ready', { world: worldName });
  }

  /**
   * World → home: change the channel back. Snapshot the world shell (static
   * DOM — cheap), prime the home video underneath, then run the channel wipe
   * with that snapshot as the outgoing picture. At `onCoverReady` the world
   * DOM is torn down beneath the opaque canvas, so the wipe dissolves onto the
   * real home page — live video, glitchGL and all. Breadcrumb + logo use this;
   * browser Back uses `close({ force: true })`.
   */
  async closeWithPixelReveal() {
    if (WANTS_REDUCED_WIPE) {
      this.close();
      return;
    }
    if (!this._open || this._closing) {
      transitionLog('world:closePixel:skip', { open: this._open, closing: this._closing });
      return;
    }
    if (!this._shell) this.init();
    this._closing = true;
    try {
      const w = window.innerWidth;
      const h = window.innerHeight;
      if (w < 1 || h < 1) throw null;

      if (document.fonts?.ready) {
        try { await document.fonts.ready; } catch { /* ignore */ }
      }
      let worldCanvas = null;
      try {
        // a world that can snapshot itself (the game reads its own GL frame)
        // skips the DOM capture, which needs a preserved drawing buffer
        if (typeof this._instance?.snapshot === 'function') {
          worldCanvas = await this._instance.snapshot({ width: w, height: h });
        }
      } catch (e) {
        console.warn('[WorldManager] world.snapshot failed, capturing DOM instead:', e);
        worldCanvas = null;
      }
      try {
        if (!worldCanvas) worldCanvas = await toCanvas(this._shell, {
          cacheBust: true,
          backgroundColor: '#070707',
          width: w,
          height: h,
          style: { transform: 'none', width: `${w}px`, height: `${h}px` },
        });
      } catch (e) {
        console.warn('[WorldManager] world snapshot failed, wiping from solid:', e);
        transitionLog('world:closePixel:snapshot_fail', { error: String(e) });
      }

      // Home is still hidden under the overlay — get it ready there.
      this._scrollHomeTop();
      const bg = getBackgroundManager();
      if (bg) {
        try { await bg.prepareForHomeTransition(); } catch (e) { console.warn('[WorldManager] prepareForHomeTransition:', e); }
      }

      await runChannelWipe({
        source: worldCanvas,
        fit: 'stretch',
        onCoverReady: () => {
          // Invisible swap: the canvas above shows the world snapshot.
          this._tearDownWorldDom();
        },
      });
      if (!this._closing) return; // interrupted by a forced close
      this._finalizeFromCloseReveal();
    } catch (e) {
      if (e) console.warn('[WorldManager] closeWithPixelReveal:', e);
      this._closing = false;
      this.close({ force: true });
    }
  }

  _finalizeFromCloseReveal() {
    this._tearDownWorldDom();
    this._open = false;
    this._closing = false;
    this._clearInPlaceUrl({ skipHistory: false });
    transitionLog('world:closePixel:done', { path: location.pathname });
    if (window.lenis?.start) window.lenis.start();
  }

  /**
   * Instant overlay close (popstate, reduced motion, or error fallbacks).
   * @param {boolean} [o.force] — if true, can interrupt a pixel-reveal exit (e.g. browser Back).
   */
  close(options = {}) {
    const { skipHistory = false, force = false } = options;
    if (!this._open) {
      return;
    }
    if (this._closing && !force) {
      transitionLog('world:close:skipped', { open: this._open, closing: this._closing, skipHistory, force });
      return;
    }
    this._closing = true;
    transitionLog('world:close:start', { skipHistory, path: location.pathname, force });

    if (force) {
      document.querySelectorAll('.world-channel-wipe').forEach((n) => n.remove());
    }

    if (!this._overlay) {
      transitionLog('world:close:hard', { reason: 'no overlay' });
      this._hardClose();
      return;
    }

    this._tearDownWorldDom();
    this._open = false;
    this._closing = false;
    this._clearInPlaceUrl({ skipHistory });
    transitionLog('world:close:done', { skipHistory, path: location.pathname });
    if (window.lenis?.start) window.lenis.start();
  }

  _hardClose() {
    this._tearDownWorldDom();
    this._open = false;
    this._closing = false;
    this._clearInPlaceUrl({ hard: true });
    transitionLog('world:close:hard_done', { path: location.pathname });
    if (window.lenis?.start) window.lenis.start();
  }
}

export const WorldManager = new WorldManagerClass();
